import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CloudCog, Languages, LoaderCircle, MessagesSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ThemeSwitcher from '../components/ThemeSwitcher';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [maxState, setMaxState] = useState(null);
  const [maxLoading, setMaxLoading] = useState(false);
  const [maxError, setMaxError] = useState('');
  const [maxPolling, setMaxPolling] = useState(false);
  const pollTimer = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language;

  useEffect(() => () => clearInterval(pollTimer.current), []);

  const finishLogin = (accessToken) => {
    login(accessToken);
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      navigate(payload.is_admin ? '/admin' : '/', { replace: true });
    } catch {
      navigate('/', { replace: true });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.login(username, password);
      finishLogin(data.access_token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startMaxLogin = async () => {
    setMaxError('');
    setMaxLoading(true);
    try {
      const data = await api.maxLoginInit();
      setMaxState(data);
      setMaxPolling(true);
      pollTimer.current = setInterval(async () => {
        try {
          const poll = await api.maxPoll(data.state);
          if (poll.confirmed && poll.access_token) {
            clearInterval(pollTimer.current);
            setMaxPolling(false);
            finishLogin(poll.access_token);
          }
        } catch (err) {
          if (err.status === 410) {
            clearInterval(pollTimer.current);
            setMaxPolling(false);
            setMaxState(null);
            setMaxError(t('login.maxExpired'));
          }
        }
      }, 2000);
    } catch (err) {
      setMaxError(err.message);
    } finally {
      setMaxLoading(false);
    }
  };

  const cancelMaxLogin = () => {
    clearInterval(pollTimer.current);
    setMaxPolling(false);
    setMaxState(null);
    setMaxError('');
  };

  return (
    <main className="auth-page">
      <section className="card auth-card" aria-labelledby="login-title">
        <div className="card-body">
          <div className="auth-toolbar">
            <ThemeSwitcher compact />
            <button type="button" className="icon-btn" onClick={() => i18n.changeLanguage(lang === 'ru' ? 'en' : 'ru')} title={t('common.switchLanguage')} aria-label={t('common.switchLanguage')}>
              <Languages className="icon" aria-hidden="true" />
            </button>
          </div>
          <div className="auth-brand">
            <img className="auth-logo" src="/logo.png" alt="" />
            <div className="auth-kicker"><CloudCog className="icon d-inline me-2" aria-hidden="true" />Geeklab Cloud</div>
            <h1 id="login-title" className="auth-title">{t('login.title')}</h1>
          </div>

          {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label" htmlFor="login-username">{t('login.username')}</label>
              <input id="login-username" className="form-control" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" autoFocus required />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="login-password">{t('login.password')}</label>
              <input id="login-password" className="form-control" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
            </div>
            <button className="btn btn-primary w-100" type="submit" disabled={loading}>
              {loading && <LoaderCircle className="icon spin" aria-hidden="true" />}
              {loading ? t('login.submitting') : t('login.submit')}
            </button>
          </form>

          <div className="auth-divider"><span>{t('login.or')}</span></div>
          {!maxState ? (
            <>
              {maxError && <div className="alert alert-danger py-2 small" role="alert">{maxError}</div>}
              <button type="button" className="btn btn-outline-primary w-100" onClick={startMaxLogin} disabled={maxLoading}>
                {maxLoading ? <LoaderCircle className="icon spin" aria-hidden="true" /> : <MessagesSquare className="icon" aria-hidden="true" />}
                {maxLoading ? t('login.submitting') : t('login.maxBtn')}
              </button>
            </>
          ) : (
            <div className="auth-provider">
              {maxState.bot_username && (() => {
                const deepLink = `https://max.ru/${maxState.bot_username}?start=${maxState.state}`;
                const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=8&data=${encodeURIComponent(deepLink)}`;
                return (
                  <div className="mb-3">
                    <img src={qrUrl} alt={t('login.maxQrAlt')} width={160} height={160} className="rounded" />
                    <p className="mb-0 mt-2 small text-muted">{t('login.maxQrHint')}</p>
                  </div>
                );
              })()}
              <p className="mb-1 small text-muted">{t('login.maxOrManually')}</p>
              <code className="fs-5 fw-bold text-primary">{maxState.state}</code>
              <p className="mb-3 small text-muted">{t('login.maxSendCmd')} <code>/login {maxState.state}</code></p>
              {maxPolling && <p className="small text-muted d-flex align-items-center justify-content-center gap-2"><LoaderCircle className="icon spin" aria-hidden="true" />{t('login.maxWaiting')}</p>}
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelMaxLogin}>{t('login.maxCancel')}</button>
            </div>
          )}

          <div className="auth-divider" aria-hidden="true" />
          <p className="text-center mb-0 small">{t('login.noAccount')}{' '}<Link to="/register">{t('login.register')}</Link></p>
        </div>
      </section>
    </main>
  );
}
